import API from "./API";

import BaseService from "./BaseService";
import AppConfig from "../config/app";

class UserService extends BaseService {
  constructor() {
    super();
  }

  /**
   * Sends a login request with the provided email and password.
   *
   * @function
   * @param {Object} data - The user's login information.
   * @param {string} data.email - The user's email address.
   * @param {string} data.password - The user's password.
   * @returns {Promise<import("@/types/data/LoginResponse.mjs").LoginResponse>}
   *
   * @example
   * login({ email: 'user@example.com', password: 'password123' });
   */
  login(data) {
    if (AppConfig.demoMode) {
      return API.post("demo/user/login", data);
    }
    return API.post("user/applogin", data);
  }

  refreshToken() {
    return API.post("user/refresh", null, this.getToken());
  }

  logout(data) {
    return API.post("user/logout", data, this.getToken());
  }

  loadAssets(data) {
    return API.get("userassets", null, this.getToken());
  }

  loadSites(data) {
    return API.get("sites", null, this.getToken());
  }

  loadSitesByFolderId(folderId) {
    return API.get(`designs?folder=${folderId}`, null, this.getToken());
  }

  createSite(data) {
    return API.post("sites", data, this.getToken());
  }

  loadAccount() {
    return API.get("user/account", null, this.getToken());
  }

  getAccessibleAccounts() {
    return API.get("accessible-accounts", null, this.getToken());
  }

  loadDomains(userId) {
    return API.get(`user/${userId}/domains`, null, this.getToken());
  }

  loadAvailableDomains(userId) {
    return API.get(`user/${userId}/domains/available`, null, this.getToken());
  }

  loadExports(userId, designType) {
    return API.get(`user/${userId}/exports`, { designType }, this.getToken());
  }

  loadUserStatus(userId) {
    return API.get(`user/${userId}/status`, null, this.getToken());
  }

  loadLibraryDesigns(userId, designType) {
    return API.get(`user/${userId}/library/designs`, { designType }, this.getToken());
  }

  switchAccount(id) {
    return API.post("user/switch", { id }, this.getToken());
  }

  addLibraryDesign(userId, exportId) {
    return API.post(`user/${userId}/library/designs`, { exportId }, this.getToken());
  }

  deleteAsset(userId, assetId) {
    return API.del(`user/${userId}/assets/${assetId}`, null, this.getToken());
  }

  getFolders(userId) {
    return API.get(`user/${userId}/assets/folders`, null, this.getToken());
  }

  getFolderAssets(userId, folderId) {
    return API.get(
      `user/${userId}/assets/folders${folderId == null ? "" : `/${folderId}`}`,
      null,
      this.getToken(),
    );
  }

  createFolder(userId, folderName) {
    return API.post(`user/${userId}/assets/folders`, { name: folderName }, this.getToken());
  }

  updateFolder(userId, folderId, folderInfo) {
    return API.post(
      `user/${userId}/assets/folders/${folderId}`,
      { data: folderInfo },
      this.getToken(),
    );
  }

  addAssetsToFolder(userId, folderId, assetIdList) {
    return API.put(
      `user/${userId}/assets/folders${folderId == null ? "" : `/${folderId}`}`,
      { assets: assetIdList },
      this.getToken(),
    );
  }

  deleteFolder(userId, folderId) {
    return API.del(`user/${userId}/assets/folders/${folderId}`, null, this.getToken());
  }

  domainSetup(userId, domainId, setupInfo) {
    return API.post(`user/${userId}/domains/${domainId}/setup`, setupInfo, this.getToken());
  }

  addSubdomain(userId, domainId, subdomainInfo) {
    return API.post(
      `user/${userId}/domains/${domainId}/subdomains`,
      subdomainInfo,
      this.getToken(),
    );
  }

  updateSubdomain(userId, subdomain) {
    return API.post(
      `user/${userId}/domains/${subdomain.parent_site_id}/subdomains/${subdomain.site_id}`,
      subdomain,
      this.getToken(),
    );
  }

  getDomain(userId, domainId) {
    return API.get(`/user/${userId}/domains/${domainId}`, null, this.getToken());
  }

  updateDomain(userId, domainId, domainInfo) {
    return API.post(`user/${userId}/domains/${domainId}`, domainInfo, this.getToken());
  }

  getBlogDomains(userId) {
    return API.get(`user/${userId}/blogDomains`, null, this.getToken());
  }

  getDomainRedirects(userId, domainId) {
    return API.get(`user/${userId}/domains/${domainId}/redirects`, null, this.getToken());
  }

  addDomainRedirect(userId, domainId, redirect) {
    return API.post(`user/${userId}/domains/${domainId}/redirects`, redirect, this.getToken());
  }

  updateDomainRedirect(userId, domainId, redirectId, redirect) {
    return API.post(
      `user/${userId}/domains/${domainId}/redirects/${redirectId}`,
      redirect,
      this.getToken(),
    );
  }

  removeDomainRedirect(userId, domainId, redirectId) {
    return API.del(
      `user/${userId}/domains/${domainId}/redirects/${redirectId}`,
      null,
      this.getToken(),
    );
  }

  validateWordpressLogin({ url, username, password }) {
    return API.post("services/validateBlogLogin", { username, password, url }, this.getToken());
  }

  getAccountLoginURL() {
    return API.get("services/account-app/login", null, this.getToken());
  }

  getBlogSetupRequest({ userId, blogId }) {
    return API.get(`user/${userId}/blogDomain/${blogId}/setup`, null, this.getToken());
  }

  updateBlogSetupRequest({ userId, blogSetup }) {
    return API.post(`user/${userId}/blog/setup`, blogSetup, this.getToken());
  }

  /**
   * Creates a new site designs folder.
   *
   * @param {Object} data - The data for the new folder (e.g., { name: string, ... }).
   * @returns {Promise} - A promise resolving to the API response.
   */
  createSiteDesignsFolder(data) {
    return API.post("designs/folders", data, this.getToken());
  }

  /**
   * Updates an existing site designs folder.
   *
   * @param {Object} params - Parameters for folder update.
   * @param {number|string} params.folderId - The ID of the folder to update.
   * @param {Object} params.data - Updated data for the folder (e.g., { name: string, ... }).
   * @returns {Promise} - A promise resolving to the API response.
   */
  updateSiteDesignsFolder({ folderId, data }) {
    return API.patch(`designs/folders/${folderId}`, data, this.getToken());
  }

  /**
   * Deletes a site designs folder.
   *
   * @param {Object} params - Parameters for folder deletion.
   * @param {number|string} params.folderId - The ID of the folder to delete.
   * @returns {Promise} - A promise resolving to the API response.
   */
  deleteSiteDesignsFolder({ folderId }) {
    return API.del(`designs/folders/${folderId}`, null, this.getToken());
  }

  /**
   * Retrieves all site designs folders.
   *
   * @returns {Promise} - A promise resolving to the API response containing the list of folders.
   */
  getSiteDesignsFolders() {
    return API.get("designs/folders", null, this.getToken());
  }

  /**
   * Retrieves the BDOW! sites reachable by the caller's connected BDOW account.
   * Resolves to an empty array when there is no (or a dead) BDOW connection.
   *
   * @returns {Promise<import("@/types/data/BdowSite.mjs").BdowSite[]>}
   */
  getBdowSites() {
    return API.get("integrations/bdow/sites", null, this.getToken());
  }

  /**
   * Mints a single-use BDOW! sign-in link for the caller's connected BDOW account.
   * Each link is spent on first use, so mint a fresh one per navigation.
   *
   * @param {Object} [params]
   * @param {string} [params.siteId] - When supplied, the link lands on that BDOW site;
   *                                   otherwise it lands on the BDOW dashboard.
   * @returns {Promise<{ url: string }>}
   */
  createBdowLoginLink({ siteId } = {}) {
    return API.post("integrations/bdow/login", siteId ? { siteId } : {}, this.getToken());
  }

  /**
   * Assigns one or more sites to one or more folders.
   * Sends all site IDs in a single batch request to the API endpoint.
   *
   * @param {Object} params - The parameters for assignment.
   * @param {number[]} params.siteIds - An array of site IDs to move (numbers).
   * @param {number[]} params.folderIds - An array of folder IDs to assign the sites to (numbers).
   * @returns {Promise} The API response promise with results for each site.
   */
  async assignSitesToFolder({ siteIds, folderIds }) {
    try {
      const response = await API.post(
        "designs/move",
        { designIds: siteIds, moveToFolderId: folderIds },
        this.getToken(),
      );
      return response;
    } catch (error) {
      return {
        data: {
          successful: [],
          failed: siteIds,
          errors: [error.message || "Failed to move designs"],
        },
        success: false,
      };
    }
  }
}

export default new UserService();
